import {switchMap} from "rxjs/operators";
import {ofType} from "redux-observable";
import {translate} from "react-i18nify";

import * as actionTypes from "../constants/actionTypes";
import * as methods from "../constants/methods";
import * as componentTypes from "../constants/componentTypes";
import * as statsTypes from "../constants/statsTypes";
import * as handlers from "../constants/handlers";
import * as rcc from "../constants/rcc";
import * as settings from "../constants/settings";
import * as dispensingValidationActions from "../actions/dispensingValidationActions";
import * as mainTabActions from "../actions/mainTabActions";
import * as navigationActions from "../actions/navigationActions";
import * as getterHelper from "../helpers/getterHelper";
import * as translationHelper from "../helpers/translationHelper";

const reloadFormulaMetaData = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.RELOAD_FORMULA_META_DATA),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (formulaStore === undefined || formulaStore.formula === null) {
                return [];
            }

            let activeMargin = formulaStore.margin;
            if (activeMargin === null) {
                activeMargin = getterHelper.getDefaultMargin(
                    state$.value.global.allMarginList,
                    state$.value.pricing.showMargin,
                    state$.value.pricing.showMarkup,
                    state$.value.pricing.showDiscount
                );
            }

            if (formulaStore.formula.getStatsType() === statsTypes.QUICK_TINT) {
                return [
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_GET_FORMULA_META_AND_PRINT_DATA,
                            params: {
                                brand: state$.value.server.activeDatabase.name,
                                formulaColorants: formulaStore.formula.getRealColorantAmounts(),
                                formulaCorrections: null,
                                unitId: 1,
                                packageCount: formulaStore.numCanTotal,
                                marginId: activeMargin === null ? null : activeMargin.id,
                                marginLocal: activeMargin === null ? null : activeMargin.local,
                                colorantUfiCodes: getterHelper.getFormulaUfiCodes(
                                    formulaStore.formula.colorantList,
                                    state$.value.rcc.canisterList,
                                    state$.value.settings.ufiCodeLogic
                                ),
                                baseInProductUfiCode: null,
                                colorantBatchNumbers: getterHelper.getFormulaColorantBatchNumbers(
                                    formulaStore.formula.colorantList,
                                    state$.value.rcc.canisterList
                                ),
                                canisterBatchNumbers: getterHelper.getFormulaCanisterBatchNumbers(
                                    formulaStore.formula.colorantList,
                                    state$.value.rcc.canisterList,
                                    state$.value.machine.allBatchNumbersList
                                )
                            }
                        },
                        method: handlers.GET_FORMULA_META_AND_PRINT_DATA,
                        tabId: payload.tabId
                    }
                ];
            }

            if (formulaStore.product === null || formulaStore.formula.color === null || formulaStore.package === null) {
                return [];
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_FORMULA_META_AND_PRINT_DATA,
                        params: {
                            brand: state$.value.server.activeDatabase.name,
                            colorId: formulaStore.formula.color.id === 0 ? null : formulaStore.formula.color.id,
                            colorLocal: formulaStore.formula.color.local,
                            fandeckId: formulaStore.fandeck === null ? null : formulaStore.fandeck.id,
                            fandeckLocal: formulaStore.fandeck === null ? null : formulaStore.formula.color.local,
                            productId: formulaStore.product.id,
                            baseId: formulaStore.base === null ? null : formulaStore.base.id,
                            formulaColorants: formulaStore.formula.getRealColorantAmounts(),
                            formulaCorrections: formulaStore.formula.isCorrection()
                                ? formulaStore.formula.getCorrectionAmounts()
                                : null,
                            packageId: formulaStore.package.package.isCustom() ? null : formulaStore.package.id,
                            nominalAmount: formulaStore.package.package.nominalAmount,
                            fillVolume: formulaStore.package.package.filling,
                            unitId: formulaStore.package.package.unit,
                            packageCount: formulaStore.numCanTotal,
                            customPackage: formulaStore.package.package.isCustom(),
                            customPackageSize: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.nominalAmount
                                : null,
                            customPackageUnitId: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.unit
                                : null,
                            marginId: activeMargin === null ? null : activeMargin.id,
                            marginLocal: activeMargin === null ? null : activeMargin.local,
                            customerId: formulaStore.customer === null ? null : formulaStore.customer.id,
                            formulaId: formulaStore.formula.id === 0 ? null : formulaStore.formula.id,
                            formulaLocal: formulaStore.formula.color.local,
                            productName: formulaStore.product.name,
                            baseName: formulaStore.base === null ? null : formulaStore.base.name,
                            fandeckName: formulaStore.fandeck === null ? null : formulaStore.fandeck.name,
                            colorName: formulaStore.formula.color.name,
                            packageName: formulaStore.package.package.name,
                            colorantUfiCodes: getterHelper.getFormulaUfiCodes(
                                formulaStore.formula.colorantList,
                                state$.value.rcc.canisterList,
                                state$.value.settings.ufiCodeLogic
                            ),
                            baseInProductUfiCode: null,
                            colorantBatchNumbers: getterHelper.getFormulaColorantBatchNumbers(
                                formulaStore.formula.colorantList,
                                state$.value.rcc.canisterList
                            ),
                            canisterBatchNumbers: getterHelper.getFormulaCanisterBatchNumbers(
                                formulaStore.formula.colorantList,
                                state$.value.rcc.canisterList,
                                state$.value.machine.allBatchNumbersList
                            )
                        }
                    },
                    method: handlers.GET_FORMULA_META_AND_PRINT_DATA,
                    tabId: payload.tabId
                }
            ];
        })
    );

const reloadFormulaMetaDataWithUfi = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.RELOAD_FORMULA_META_DATA_WITH_UFI),
        switchMap(({payload}) => {
            let formulaStore = null;
            let dispensingTabId = null;

            for (let tab of state$.value.navigation.tabList) {
                if (state$.value.formula[tab.id] !== undefined && state$.value.formula[tab.id].dispensing) {
                    formulaStore = state$.value.formula[tab.id];
                    dispensingTabId = tab.id;
                }
            }

            if (
                formulaStore === undefined ||
                formulaStore === null ||
                formulaStore.formula === null ||
                dispensingTabId === componentTypes.QUICK_TINT_TAB_ID
            ) {
                if (
                    state$.value.rcc.dispensingFormula === null ||
                    state$.value.rcc.dispensingFormula.formula === null
                ) {
                    return [];
                }

                if (state$.value.rcc.dispensingFormula.formula.getStatsType() === statsTypes.QUICK_TINT) {
                    return [
                        {
                            type: actionTypes.SERVER_REQUEST,
                            payload: {
                                type: methods.METHOD_GET_FORMULA_META_AND_PRINT_DATA,
                                params: {
                                    brand: state$.value.server.activeDatabase.name,
                                    formulaColorants:
                                        state$.value.rcc.dispensingFormula.formula.getRealColorantAmounts(),
                                    formulaCorrections: null,
                                    unitId: 1,
                                    packageCount: 1,
                                    marginId: null,
                                    marginLocal: null,
                                    colorantUfiCodes: getterHelper.getFormulaUfiCodes(
                                        state$.value.rcc.dispensingFormula.formula.colorantList,
                                        state$.value.rcc.canisterList,
                                        state$.value.settings.ufiCodeLogic
                                    ),
                                    baseInProductUfiCode: payload.ufiCode,
                                    colorantBatchNumbers: getterHelper.getFormulaColorantBatchNumbers(
                                        formulaStore.formula.colorantList,
                                        state$.value.rcc.canisterList
                                    ),
                                    canisterBatchNumbers: getterHelper.getFormulaCanisterBatchNumbers(
                                        formulaStore.formula.colorantList,
                                        state$.value.rcc.canisterList,
                                        state$.value.machine.allBatchNumbersList
                                    )
                                }
                            },
                            method: handlers.GET_FORMULA_META_AND_PRINT_DATA_WITH_UFI,
                            tabId: componentTypes.QUICK_TINT_TAB_ID
                        }
                    ];
                }

                if (state$.value.rcc.machine.dialog === null) {
                    return [];
                }

                return [
                    {
                        type: actionTypes.RCC_REQUEST,
                        payload: {
                            type: methods.METHOD_MACHINE_DIALOG_BUTTON_CLICK,
                            params: {
                                dialogID: state$.value.rcc.machine.dialog.id,
                                msgBtn: rcc.RCC_BTN_CONTINUE
                            }
                        },
                        method: handlers.MACHINE_DIALOG_BUTTON_CLICK,
                        tabId: 0,
                        safe: false
                    }
                ];
            }

            if (formulaStore.product === null || formulaStore.formula.color === null || formulaStore.package === null) {
                return [];
            }

            let activeMargin = formulaStore.margin;
            if (activeMargin === null) {
                activeMargin = getterHelper.getDefaultMargin(
                    state$.value.global.allMarginList,
                    state$.value.pricing.showMargin,
                    state$.value.pricing.showMarkup,
                    state$.value.pricing.showDiscount
                );
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_FORMULA_META_AND_PRINT_DATA,
                        params: {
                            brand: state$.value.server.activeDatabase.name,
                            colorId: formulaStore.formula.color.id === 0 ? null : formulaStore.formula.color.id,
                            colorLocal: formulaStore.formula.color.local,
                            fandeckId: formulaStore.fandeck === null ? null : formulaStore.fandeck.id,
                            fandeckLocal: formulaStore.fandeck === null ? null : formulaStore.formula.color.local,
                            productId: formulaStore.product.id,
                            baseId: formulaStore.base === null ? null : formulaStore.base.id,
                            formulaColorants: formulaStore.formula.getRealColorantAmounts(),
                            formulaCorrections: formulaStore.formula.isCorrection()
                                ? formulaStore.formula.getCorrectionAmounts()
                                : null,
                            packageId: formulaStore.package.package.isCustom() ? null : formulaStore.package.id,
                            nominalAmount: formulaStore.package.package.nominalAmount,
                            fillVolume: formulaStore.package.package.filling,
                            unitId: formulaStore.package.package.unit,
                            packageCount: formulaStore.numCanTotal,
                            customPackage: formulaStore.package.package.isCustom(),
                            customPackageSize: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.nominalAmount
                                : null,
                            customPackageUnitId: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.unit
                                : null,
                            marginId: activeMargin === null ? null : activeMargin.id,
                            marginLocal: activeMargin === null ? null : activeMargin.local,
                            customerId: formulaStore.customer === null ? null : formulaStore.customer.id,
                            formulaId: formulaStore.formula.id === 0 ? null : formulaStore.formula.id,
                            formulaLocal: formulaStore.formula.color.local,
                            productName: formulaStore.product.name,
                            baseName: formulaStore.base === null ? null : formulaStore.base.name,
                            fandeckName: formulaStore.fandeck === null ? null : formulaStore.fandeck.name,
                            colorName: formulaStore.formula.color.name,
                            packageName: formulaStore.package.package.name,
                            colorantUfiCodes: getterHelper.getFormulaUfiCodes(
                                formulaStore.formula.colorantList,
                                state$.value.rcc.canisterList,
                                state$.value.settings.ufiCodeLogic
                            ),
                            baseInProductUfiCode: payload.ufiCode,
                            colorantBatchNumbers: getterHelper.getFormulaColorantBatchNumbers(
                                formulaStore.formula.colorantList,
                                state$.value.rcc.canisterList
                            ),
                            canisterBatchNumbers: getterHelper.getFormulaCanisterBatchNumbers(
                                formulaStore.formula.colorantList,
                                state$.value.rcc.canisterList,
                                state$.value.machine.allBatchNumbersList
                            )
                        }
                    },
                    method: handlers.GET_FORMULA_META_AND_PRINT_DATA_WITH_UFI,
                    tabId: dispensingTabId
                }
            ];
        })
    );

const setNumberOfPackages = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SET_NUMBER_OF_PACKAGES),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (formulaStore === undefined || formulaStore.formula === null) {
                return [];
            }

            return [
                {
                    type: actionTypes.RELOAD_FORMULA_META_DATA,
                    payload: {
                        tabId: payload.tabId
                    }
                },
                {
                    type: actionTypes.MACHINE_VALIDATE_DISPENSED_FORMULA,
                    payload: {
                        tabId: payload.tabId,
                        formula: formulaStore.formula.getColorantVolumes(),
                        numCanDone: formulaStore.numCanDone,
                        numCanTotal: formulaStore.numCanTotal,
                        customCan: formulaStore.package?.isCustom() ?? false
                    }
                }
            ];
        })
    );

const setPackage = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MAIN_TAB_SET_PACKAGE),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (
                formulaStore === undefined ||
                formulaStore.product === null ||
                formulaStore.fandeck === null ||
                formulaStore.color === null ||
                formulaStore.formula === null ||
                formulaStore.base === null ||
                formulaStore.base.baseInProductId === 0 ||
                payload.selectedPackage === null ||
                payload.previousPackage === null
            ) {
                return [
                    {
                        type: actionTypes.MAIN_TAB_SAVE_PACKAGE_IMAGE,
                        payload: {
                            tabId: payload.tabId,
                            imageId: -1
                        }
                    }
                ];
            }

            let recalculateParams = {
                baseInProductId: formulaStore.base.baseInProductId,
                baseInProductPackageId: payload.selectedPackage.package.isCustom() ? null : payload.selectedPackage.id,
                customPackage: payload.selectedPackage.package.isCustom(),
                customPackageSize: payload.selectedPackage.package.isCustom()
                    ? payload.selectedPackage.package.nominalAmount
                    : null,
                customPackageUnit: payload.selectedPackage.package.isCustom()
                    ? payload.selectedPackage.package.unit
                    : null,
                isLocal: formulaStore.color.local
            };

            let recalculateNext = null;

            if (formulaStore.formula.modified || formulaStore.formula.scaling !== 1) {
                recalculateNext = {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_RECALCULATE_FORMULA,
                        params: {
                            formulaId: null,
                            formula: {
                                colorants: formulaStore.formula.getRealColorantAmounts(),
                                baseFillVolumeMl: payload.previousPackage.fillingMl,
                                formulaTypeId: formulaStore.formula.formulaTypeId
                            },
                            formulaForCustomPackage: payload.previousPackage.package.isCustom(),
                            ...recalculateParams
                        }
                    },
                    method: handlers.RECALCULATE_MODIFIED_FORMULA,
                    tabId: payload.tabId
                };
            }

            let requests = [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_RECALCULATE_FORMULA,
                        params: {
                            formulaId: formulaStore.formula.id,
                            formula:
                                formulaStore.formula.id !== null
                                    ? null
                                    : {
                                          colorants: formulaStore.formula.getColorantAmounts(),
                                          baseFillVolumeMl: payload.previousPackage.fillingMl,
                                          formulaTypeId: formulaStore.formula.formulaTypeId
                                      },
                            formulaForCustomPackage: payload.previousPackage.package.isCustom(),
                            ...recalculateParams
                        }
                    },
                    method: handlers.RECALCULATE_FORMULA,
                    tabId: payload.tabId,
                    next: recalculateNext
                }
            ];

            if (payload.selectedPackage.package.isCustom()) {
                requests.push({
                    type: actionTypes.MAIN_TAB_SAVE_PACKAGE_IMAGE,
                    payload: {
                        tabId: payload.tabId,
                        imageId: -1
                    }
                });
            }

            if (!payload.selectedPackage.package.isCustom()) {
                requests.push({
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_PRODUCT_IMAGE,
                        params: {
                            productName: formulaStore.product.name,
                            baseName: formulaStore.base.name,
                            packageName: payload.selectedPackage.package.name,
                            online: state$.value.settings.imagesSource === settings.SOURCE_ONLINE
                        }
                    },
                    method: handlers.GET_PRODUCT_IMAGE,
                    tabId: payload.tabId
                });
            }

            return requests;
        })
    );

const setCustomer = (action$) =>
    action$.pipe(
        ofType(actionTypes.MAIN_TAB_SET_CUSTOMER),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.RELOAD_FORMULA_META_DATA,
                    payload: {
                        tabId: payload.tabId
                    }
                }
            ];
        })
    );

const deleteCustomer = (action$) =>
    action$.pipe(
        ofType(actionTypes.MAIN_TAB_DELETE_CUSTOMER),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.RELOAD_FORMULA_META_DATA,
                    payload: {
                        tabId: payload.tabId
                    }
                }
            ];
        })
    );

const setMargin = (action$) =>
    action$.pipe(
        ofType(actionTypes.MAIN_TAB_SET_MARGIN),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.RELOAD_FORMULA_META_DATA,
                    payload: {
                        tabId: payload.tabId
                    }
                }
            ];
        })
    );

const reloadFormulaData = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MAIN_TAB_RELOAD_FORMULA_DATA),
        switchMap(() => {
            let mainTabs = state$.value.navigation.tabList.filter(
                (tab) => getterHelper.isFormulaTab(tab.type) || tab.type === componentTypes.FAVORITES_TAB
            );
            let tab,
                formulaStore,
                requests = [];
            for (tab of mainTabs) {
                formulaStore = state$.value.formula[tab.id];
                if (
                    formulaStore !== undefined &&
                    formulaStore.product !== null &&
                    formulaStore.fandeck !== null &&
                    formulaStore.color !== null &&
                    formulaStore.formula !== null
                ) {
                    requests.push({
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_GET_CALCULATED_FORMULA,
                            params: {
                                productId: formulaStore.product.id,
                                fandeckId: formulaStore.fandeck.id,
                                colorId: formulaStore.color.id,
                                isLocal: formulaStore.color.local
                            }
                        },
                        method: handlers.GET_CALCULATED_FORMULA,
                        tabId: tab.id
                    });
                }
            }

            return requests;
        })
    );

const updateFormulaFromHistoricalFormulas = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.UPDATE_FORMULA_FROM_HISTORICAL_FORMULA),
        switchMap(({payload}) => {
            let actions = [
                {
                    type: actionTypes.RELOAD_FORMULA_META_DATA,
                    payload: {
                        tabId: payload.tabId
                    }
                }
            ];

            let formulaStore = state$.value.formula[payload.tabId];

            if (formulaStore === undefined) {
                return actions;
            }

            if (formulaStore.product !== null && formulaStore.base !== null && formulaStore.package !== null) {
                actions.push({
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_PRODUCT_IMAGE,
                        params: {
                            productName: formulaStore.product.name,
                            baseName: formulaStore.base.name,
                            packageName: formulaStore.package.package.name,
                            online: state$.value.settings.imagesSource === settings.SOURCE_ONLINE
                        }
                    },
                    method: handlers.GET_PRODUCT_IMAGE,
                    tabId: payload.tabId
                });
            }

            if (formulaStore.moreInfoData === undefined) {
                return actions;
            }

            if (formulaStore.moreInfoData !== null && formulaStore.moreInfoData.length > 0) {
                for (let moreInfo of formulaStore.moreInfoData) {
                    if (moreInfo.hasImage && window.imageStore.getFormulaNoteImageById(moreInfo.id) === null) {
                        actions.push({
                            type: actionTypes.SERVER_REQUEST,
                            payload: {
                                type: methods.METHOD_GET_FORMULA_NOTE_IMAGE,
                                params: {
                                    formulaNoteId: moreInfo.id,
                                    online: state$.value.settings.imagesSource === settings.SOURCE_ONLINE
                                }
                            },
                            method: handlers.GET_FORMULA_NOTE_IMAGE,
                            tabId: componentTypes.NO_SKIP_TAB_ID
                        });
                    }
                }
            }

            return actions;
        })
    );

const getClosestColorWithPrices = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.GET_CLOSEST_COLOR_WITH_PRICES),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (
                formulaStore === undefined ||
                formulaStore.product === null ||
                formulaStore.fandeck === null ||
                formulaStore.color === null
            ) {
                return [];
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_CLOSEST_COLOR_WITH_PRICES,
                        params: {
                            compColorId: formulaStore.color.id,
                            compFandeckId: formulaStore.fandeck.id,
                            productId: formulaStore.product.id,
                            fandeckId: null,
                            packageId: formulaStore.package.isCustom() ? null : formulaStore.package.package.id,
                            customPackage: formulaStore.package.isCustom(),
                            customPackageSize: formulaStore.package.isCustom()
                                ? formulaStore.package.package.nominalAmount
                                : null,
                            customPackageunit: formulaStore.package.isCustom()
                                ? formulaStore.package.package.unit
                                : null,
                            marginId: formulaStore.margin === null ? null : formulaStore.margin.id,
                            marginLocal: formulaStore.margin === null ? false : formulaStore.margin.local,
                            cie_de_2000: 5
                        }
                    },
                    method: handlers.GET_CLOSEST_COLOR_WITH_PRICES,
                    tabId: payload.tabId
                },
                {
                    type: actionTypes.NAVIGATION_OPEN_MODAL,
                    payload: {
                        tabId: payload.tabId,
                        type: componentTypes.LOADING_MODAL,
                        params: {
                            message: translate("dialog.cheaperColorSearch"),
                            onCancel: {
                                type: actionTypes.SKIP_WEBSOCKET_REQUEST,
                                payload: {
                                    tabId: payload.tabId,
                                    method: handlers.GET_CLOSEST_COLOR_WITH_PRICES
                                }
                            }
                        }
                    }
                }
            ];
        })
    );

const startFormulaDispensing = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MAIN_TAB_START_FORMULA_DISPENSING),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (
                formulaStore === undefined ||
                formulaStore.product === null ||
                formulaStore.fandeck === null ||
                formulaStore.color === null ||
                formulaStore.formula === null
            ) {
                return [];
            }

            return [
                dispensingValidationActions.getValidationActionsSequence(
                    payload.tabId,
                    mainTabActions.addToStatistics,
                    formulaStore.validation,
                    state$.value.settings.dispensingWithErrorsEnabled,
                    formulaStore.formula.realColorantList,
                    formulaStore.numCanTotal - formulaStore.numCanDone
                )
            ];
        })
    );

const startQuickDispensing = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MAIN_TAB_START_QUICK_DISPENSING),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (formulaStore === undefined || formulaStore.formula === null) {
                return [];
            }

            return [
                dispensingValidationActions.getQuickTintValidationActionsSequence(
                    payload.tabId,
                    formulaStore.validation,
                    formulaStore.formula.realColorantList,
                    formulaStore.numCanTotal - formulaStore.numCanDone
                )
            ];
        })
    );

const createOrder = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MAIN_TAB_CREATE_ORDER),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (
                formulaStore === undefined ||
                formulaStore.formula === null ||
                formulaStore.formula.modified ||
                formulaStore.package === null ||
                formulaStore.package.package.id === -1
            ) {
                return [];
            }

            if (formulaStore.validation !== null && formulaStore.validation.isFormulaInvalid()) {
                return [
                    navigationActions.openConfirmationModal(
                        componentTypes.MODAL_NO_TAB,
                        mainTabActions.getCompatibleOrdersAndOpenModal(payload.tabId),
                        translationHelper.getFormulaErrorTranslations(formulaStore.validation.getMainErrors()),
                        translate("help.formulaValidation.warningConfirmBtn")
                    )
                ];
            }

            return [mainTabActions.getCompatibleOrdersAndOpenModal(payload.tabId)];
        })
    );

const reloadFormulaAfterPartialUpdate = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MAIN_TAB_RELOAD_AFTER_PARTIAL_UPDATE),
        switchMap(() => {
            let formulaTabs = state$.value.navigation.tabList.filter((tab) => tab.type === componentTypes.MAIN_TAB);
            let actions = [];

            let tab;
            for (tab of formulaTabs) {
                if (
                    state$.value.searchPanel[tab.id] !== undefined &&
                    state$.value.searchPanel[tab.id].type === componentTypes.SEARCH_PANEL_DEFAULT
                ) {
                    actions.push({
                        type: actionTypes.MAIN_TAB_RELOAD_REQUIRED,
                        payload: {
                            tabId: tab.id
                        }
                    });
                }
            }

            return actions;
        })
    );

export default [
    reloadFormulaMetaData,
    reloadFormulaMetaDataWithUfi,
    setNumberOfPackages,
    setCustomer,
    setPackage,
    setMargin,
    reloadFormulaData,
    updateFormulaFromHistoricalFormulas,
    deleteCustomer,
    getClosestColorWithPrices,
    startFormulaDispensing,
    startQuickDispensing,
    createOrder,
    reloadFormulaAfterPartialUpdate
];
